/**
 * SpellCard Component
 *
 * Displays D&D 5e spell information in a styled card format.
 * Supports compact, full, and mini variants.
 */

import type { Open5eSpell } from '@machina/core';
import { Icon, type IconName } from './Icon';

// =============================================================================
// SCHOOL COLORS & ICONS
// =============================================================================

const SCHOOL_CONFIG: Record<string, { color: string; icon: IconName }> = {
  Abjuration: { color: '#60a5fa', icon: 'shield' },
  Conjuration: { color: '#a78bfa', icon: 'sparkle' },
  Divination: { color: '#fbbf24', icon: 'eye' },
  Enchantment: { color: '#f472b6', icon: 'heart' },
  Evocation: { color: '#ef4444', icon: 'attack' },
  Illusion: { color: '#818cf8', icon: 'moon' },
  Necromancy: { color: '#84cc16', icon: 'skull' },
  Transmutation: { color: '#22d3d3', icon: 'crystal' },
};

function getSchoolConfig(school: string) {
  return SCHOOL_CONFIG[school] || { color: 'var(--color-accent)', icon: 'wand' as IconName };
}

function getLevelText(level: number): string {
  if (level === 0) return 'Cantrip';
  const suffix = level === 1 ? 'st' : level === 2 ? 'nd' : level === 3 ? 'rd' : 'th';
  return `${level}${suffix} Level`;
}

// =============================================================================
// COMPONENT ICONS
// =============================================================================

function ComponentBadge({ type, material }: { type: 'V' | 'S' | 'M'; material?: string }) {
  const titles: Record<string, string> = {
    V: 'Verbal',
    S: 'Somatic',
    M: material ? `Material: ${material}` : 'Material',
  };

  return (
    <span
      title={titles[type]}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        borderRadius: 'var(--radius-sm)',
        backgroundColor: 'rgba(255,255,255,0.1)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 600,
        color: type === 'M' && material ? 'var(--color-warning)' : 'var(--color-text-secondary)',
        cursor: 'help',
      }}
    >
      {type}
    </span>
  );
}

// =============================================================================
// MINI VARIANT (for tooltips)
// =============================================================================

interface SpellCardMiniProps {
  spell: Open5eSpell;
}

export function SpellCardMini({ spell }: SpellCardMiniProps) {
  const { color } = getSchoolConfig(spell.school);

  return (
    <div style={{
      padding: 'var(--spacing-xs) var(--spacing-sm)',
      backgroundColor: 'var(--color-surface)',
      borderLeft: `3px solid ${color}`,
      borderRadius: 'var(--radius-sm)',
      fontSize: 'var(--font-size-xs)',
    }}>
      <div style={{ fontWeight: 600, color: 'var(--color-text)' }}>{spell.name}</div>
      <div style={{ color: 'var(--color-text-muted)' }}>
        {getLevelText(spell.level_int)} {spell.school}
      </div>
    </div>
  );
}

// =============================================================================
// COMPACT VARIANT (for lists)
// =============================================================================

interface SpellCardCompactProps {
  spell: Open5eSpell;
  onClick?: () => void;
  selected?: boolean;
}

export function SpellCardCompact({ spell, onClick, selected }: SpellCardCompactProps) {
  const { color, icon } = getSchoolConfig(spell.school);

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        padding: 'var(--spacing-sm) var(--spacing-md)',
        backgroundColor: selected ? `${color}20` : 'transparent',
        borderLeft: `3px solid ${selected ? color : 'transparent'}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
      }}
    >
      <Icon name={icon} size={16} color={color} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 500,
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {spell.name}
        </div>
        <div style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
        }}>
          {getLevelText(spell.level_int)} · {spell.casting_time}
        </div>
      </div>
      {spell.requires_concentration && (
        <span style={{
          fontSize: 9,
          fontWeight: 600,
          color: 'var(--color-warning)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}>
          C
        </span>
      )}
      {spell.can_be_cast_as_ritual && (
        <span style={{
          fontSize: 9,
          fontWeight: 600,
          color: 'var(--color-accent)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}>
          R
        </span>
      )}
    </div>
  );
}

// =============================================================================
// FULL VARIANT (for chat/lookup)
// =============================================================================

interface SpellCardFullProps {
  spell: Open5eSpell;
  onCast?: () => void;
  showClasses?: boolean;
}

export function SpellCardFull({ spell, onCast, showClasses = true }: SpellCardFullProps) {
  const { color, icon } = getSchoolConfig(spell.school);

  return (
    <div style={{
      backgroundColor: 'var(--color-surface)',
      borderRadius: 'var(--radius-md)',
      border: `1px solid ${color}40`,
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    }}>
      {/* Header */}
      <div style={{
        padding: 'var(--spacing-md)',
        background: `linear-gradient(135deg, ${color}20 0%, transparent 60%)`,
        borderBottom: `1px solid ${color}30`,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--spacing-sm)',
        }}>
          <div style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            backgroundColor: `${color}30`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Icon name={icon} size={18} color={color} />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 600,
              fontFamily: 'var(--font-display)',
              color: 'var(--color-text)',
            }}>
              {spell.name}
            </h3>
            <div style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)',
              fontStyle: 'italic',
            }}>
              {getLevelText(spell.level_int)} {spell.school}
            </div>
          </div>
          {(spell.requires_concentration || spell.can_be_cast_as_ritual) && (
            <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
              {spell.requires_concentration && (
                <span style={{
                  padding: '2px 6px',
                  backgroundColor: 'var(--color-warning)',
                  color: '#000',
                  fontSize: 9,
                  fontWeight: 700,
                  borderRadius: 'var(--radius-sm)',
                  textTransform: 'uppercase',
                }}>
                  Concentration
                </span>
              )}
              {spell.can_be_cast_as_ritual && (
                <span style={{
                  padding: '2px 6px',
                  backgroundColor: 'var(--color-accent)',
                  color: '#000',
                  fontSize: 9,
                  fontWeight: 700,
                  borderRadius: 'var(--radius-sm)',
                  textTransform: 'uppercase',
                }}>
                  Ritual
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 1,
        backgroundColor: 'var(--color-border)',
      }}>
        {[
          { label: 'Casting Time', value: spell.casting_time },
          { label: 'Range', value: spell.range },
          { label: 'Duration', value: spell.duration },
          { label: 'Components', value: null }, // Special handling
        ].map((stat, i) => (
          <div key={i} style={{
            padding: 'var(--spacing-sm)',
            backgroundColor: 'var(--color-background)',
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: 9,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: 'var(--color-text-muted)',
              marginBottom: 4,
            }}>
              {stat.label}
            </div>
            {stat.value ? (
              <div style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text)',
              }}>
                {stat.value}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                {spell.verbal && <ComponentBadge type="V" />}
                {spell.somatic && <ComponentBadge type="S" />}
                {spell.material && <ComponentBadge type="M" material={spell.material_specified} />}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Description */}
      <div style={{
        padding: 'var(--spacing-md)',
        fontSize: 'var(--font-size-sm)',
        lineHeight: 'var(--line-height-relaxed)',
        color: 'var(--color-text-secondary)',
      }}>
        <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{spell.desc}</p>

        {spell.higher_level && (
          <div style={{
            marginTop: 'var(--spacing-md)',
            padding: 'var(--spacing-sm)',
            backgroundColor: 'rgba(255,255,255,0.05)',
            borderRadius: 'var(--radius-sm)',
            borderLeft: `3px solid ${color}`,
          }}>
            <div style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              textTransform: 'uppercase',
              color: color,
              marginBottom: 4,
            }}>
              At Higher Levels
            </div>
            <p style={{ margin: 0 }}>{spell.higher_level}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: 'var(--spacing-sm) var(--spacing-md)',
        borderTop: `1px solid var(--color-border)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'var(--color-background)',
      }}>
        {showClasses && spell.classes && spell.classes.length > 0 && (
          <div style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
          }}>
            {spell.classes.join(', ')}
          </div>
        )}
        {onCast && (
          <button
            onClick={onCast}
            style={{
              padding: 'var(--spacing-xs) var(--spacing-md)',
              backgroundColor: color,
              color: '#000',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-xs)',
            }}
          >
            <Icon name="dice" size={14} />
            Cast
          </button>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// DEFAULT EXPORT (Full variant)
// =============================================================================

export function SpellCard(props: SpellCardFullProps) {
  return <SpellCardFull {...props} />;
}
